import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DataSection from './Datasection';

const FreeClassesSection = ({ showNotification }) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({
    video_url: '',
    display_order: 0,
    active_status: true,
    image: null
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [formLoading, setFormLoading] = useState(false);

  const columns = [
    { key: 'id', header: 'ID' },
    { key: 'imagePreview', header: 'Image' },
    { key: 'video_url', header: 'Video URL' },
    { key: 'activeStatus', header: 'Status' },
    { key: 'display_order', header: 'Order' }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get('https://apissc.sainikschoolcadet.com/api/free-classes');
      setData(response.data);
    } catch (error) {
      console.error('Error fetching free classes:', error);
      showNotification('Failed to load free classes', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      video_url: '',
      display_order: 0,
      active_status: true,
      image: null
    });
    setImagePreview(null);
    setErrors({});
    setSelectedItem(null);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setFormData({
      video_url: item.video_url || '',
      display_order: item.display_order || 0,
      active_status: item.active === true || item.active === 1 || item.activeStatus === 'Active',
      image: null
    });
    if (item.imagePreview) {
      setImagePreview(item.imagePreview);
    }
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this free class?')) {
      try {
        await axios.delete(`https://apissc.sainikschoolcadet.com/api/free-classes/${id}`);
        setData(data.filter(item => item.id !== id));
        showNotification('Free class deleted successfully');
      } catch (error) {
        console.error('Error deleting free class:', error);
        showNotification('Failed to delete free class', 'error');
      }
    }
  };

  const handleAdd = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    
    if (type === 'file') {
      const file = files[0];
      if (file) {
        setFormData({ ...formData, [name]: file });
        
        // Create preview for the selected image
        const reader = new FileReader();
        reader.onload = (e) => {
          setImagePreview(e.target.result);
        };
        reader.readAsDataURL(file);
      }
    } else if (type === 'checkbox') {
      setFormData({ ...formData, [name]: checked });
    } else if (name === 'display_order') {
      setFormData({ ...formData, [name]: parseInt(value, 10) || 0 });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.video_url) {
      newErrors.video_url = 'Video URL is required';
    } else if (!formData.video_url.startsWith('http')) {
      newErrors.video_url = 'Please enter a valid URL';
    }
    
    if (formData.display_order < 0) {
      newErrors.display_order = 'Display order must be a positive number';
    }
    
    if (!selectedItem && !formData.image) {
      newErrors.image = 'Please select an image';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }
    
    setFormLoading(true);
    
    try {
      let imagePath = null;
      
      // First, handle image upload if there's a new image
      if (formData.image) {
        const imageFormData = new FormData();
        imageFormData.append('image', formData.image);
        imageFormData.append('title', `Free Class ${formData.video_url ? '- ' + formData.video_url.substring(0, 30) : ''}`);
        imageFormData.append('tag', 'free-class');
        
        try {
          const imageUploadResponse = await axios.post(
            'https://apissc.sainikschoolcadet.com/api/images',
            imageFormData,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            }
          );
          
          if (imageUploadResponse.data && imageUploadResponse.data.path) {
            imagePath = imageUploadResponse.data.path;
          } else {
            throw new Error('Image upload response did not contain a path');
          }
        } catch (imageError) {
          console.error('Error uploading image:', imageError);
          setErrors({ ...errors, image: 'Failed to upload image. Please try again.' });
          setFormLoading(false);
          return;
        }
      }
      
      // Now create/update the free class with the image path
      const submitData = {
        video_url: formData.video_url,
        display_order: formData.display_order,
        active: formData.active_status ? true : false
      };
      
      // Add image path if we have one from upload or keep existing if editing
      if (imagePath) {
        submitData.image_path = imagePath;
      }
      
      let response;
      
      if (selectedItem) {
        // Update existing item
        response = await axios.put(
          `https://apissc.sainikschoolcadet.com/api/free-classes/${selectedItem.id}`, 
          submitData
        );
      } else {
        // Create new item
        response = await axios.post(
          'https://apissc.sainikschoolcadet.com/api/free-classes',
          submitData
        );
      }
      
      if (response.data) {
        showNotification(selectedItem ? 'Free class updated successfully' : 'Free class added successfully');
        setIsModalOpen(false);
        fetchData();
      }
    } catch (error) {
      console.error('Error saving free class:', error);
      if (error.response && error.response.data && error.response.data.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({ general: 'Failed to save. Please try again.' });
      }
      showNotification('Failed to save free class', 'error');
    } finally {
      setFormLoading(false);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  // Modal component
  const FormModal = ({ isOpen, onClose, children }) => {
    if (!isOpen) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
          {children}
        </div>
      </div>
    );
  };

  return (
    <>
      <DataSection
        title="Free Classes"
        data={data}
        columns={columns}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onAdd={handleAdd}
        isLoading={isLoading}
        emptyMessage="No free classes found"
      />
      
      <FormModal isOpen={isModalOpen} onClose={closeModal}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <h2 className="text-xl font-bold mb-4">
            {selectedItem ? 'Edit Free Class' : 'Add New Free Class'}
          </h2>
          
          <div className="form-group">
            <label htmlFor="video_url" className="block mb-1 font-medium">
              Video URL *
            </label>
            <input
              type="text"
              id="video_url"
              name="video_url"
              value={formData.video_url}
              onChange={handleChange}
              className={`w-full p-2 border rounded ${errors.video_url ? 'border-red-500' : 'border-gray-300'}`}
              placeholder="https://youtube.com/video"
            />
            {errors.video_url && (
              <p className="text-red-500 text-sm mt-1">{errors.video_url}</p>
            )}
          </div>
          
          <div className="form-group">
            <label htmlFor="display_order" className="block mb-1 font-medium">
              Display Order
            </label>
            <input
              type="number"
              id="display_order"
              name="display_order"
              value={formData.display_order}
              onChange={handleChange}
              className={`w-full p-2 border rounded ${errors.display_order ? 'border-red-500' : 'border-gray-300'}`}
              min="0"
            />
            {errors.display_order && (
              <p className="text-red-500 text-sm mt-1">{errors.display_order}</p>
            )}
          </div>
          
          <div className="form-group">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                name="active_status"
                checked={formData.active_status}
                onChange={handleChange}
                className="form-checkbox h-5 w-5"
              />
              <span>Active</span>
            </label>
          </div>
          
          <div className="form-group">
            <label htmlFor="image" className="block mb-1 font-medium">
              Image {!selectedItem && '*'}
            </label>
            <input
              type="file"
              id="image"
              name="image"
              onChange={handleChange}
              className={`w-full p-2 border rounded ${errors.image ? 'border-red-500' : 'border-gray-300'}`}
              accept="image/*"
            />
            {errors.image && (
              <p className="text-red-500 text-sm mt-1">{errors.image}</p>
            )}
            
            {imagePreview && (
              <div className="mt-2">
                <p className="text-sm font-medium mb-1">Preview:</p>
                <img 
                  src={imagePreview} 
                  alt="Preview" 
                  className="w-40 h-auto object-cover border rounded"
                />
              </div>
            )}
          </div>
          
          {errors.general && (
            <div className="bg-red-100 text-red-700 p-3 rounded">
              {errors.general}
            </div>
          )}
          
          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={closeModal}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
              disabled={formLoading}
            >
              Cancel
            </button>
            
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              disabled={formLoading}
            >
              {formLoading ? (
                <span>Saving...</span>
              ) : (
                <span>{selectedItem ? 'Update' : 'Save'}</span>
              )}
            </button>
          </div>
        </form>
      </FormModal>
    </>
  );
};

export default FreeClassesSection;